
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * ExecutionServerSettings struct
 */
class ExecutionServerSettings {
  /**
   * Initialize struct
   * @param {!number=} executionPort
   * @param {!boolean=} useSsl
   * @constructor
   */
  constructor (executionPort = 6150, useSsl = false) {
    this.ExecutionPort = executionPort
    this.UseSsl = useSsl
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ExecutionServerSettings}
   * @param {!ExecutionServerSettings} other Other struct
   * @returns {!ExecutionServerSettings} This struct
   */
  copy (other) {
    if (other.ExecutionPort != null) {
      this.ExecutionPort = other.ExecutionPort
    } else {
      this.ExecutionPort = null
    }
    if (other.UseSsl != null) {
      this.UseSsl = other.UseSsl
    } else {
      this.UseSsl = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ExecutionServerSettings}
   * @returns {!ExecutionServerSettings} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ExecutionServerSettingsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ExecutionServerSettingsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ExecutionServerSettings}
   * @param {!ExecutionServerSettings} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ExecutionServerSettings)) {
      throw new TypeError('Instance of ExecutionServerSettings is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ExecutionServerSettings}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      ExecutionPort: ((this.ExecutionPort != null) ? this.ExecutionPort : null),
      UseSsl: ((this.UseSsl != null) ? this.UseSsl : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ExecutionServerSettings.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ExecutionServerSettings} other Object value
   * @returns {!ExecutionServerSettings} Created struct
   */
  static fromObject (other) {
    return new ExecutionServerSettings().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ExecutionServerSettings}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ExecutionServerSettings.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ExecutionServerSettings}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 13
  }
}

export { ExecutionServerSettings };

/**
 * Fast Binary Encoding ExecutionServerSettings field model
 */
class FieldModelExecutionServerSettings extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.ExecutionPort = new fbe.FieldModelUInt16(buffer, 4 + 4)
    this.UseSsl = new fbe.FieldModelBool(buffer, this.ExecutionPort.fbeOffset + this.ExecutionPort.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelExecutionServerSettings}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelExecutionServerSettings}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.ExecutionPort.fbeSize + this.UseSsl.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelExecutionServerSettings}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.ExecutionPort.fbeExtra + this.UseSsl.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelExecutionServerSettings}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelExecutionServerSettings.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelExecutionServerSettings}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 13
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelExecutionServerSettings}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelExecutionServerSettings}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.ExecutionPort.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ExecutionPort.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ExecutionPort.fbeSize

    if ((fbeCurrentSize + this.UseSsl.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.UseSsl.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UseSsl.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelExecutionServerSettings}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelExecutionServerSettings}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelExecutionServerSettings}
   * @param {!ExecutionServerSettings} fbeValue Default value, defaults is new ExecutionServerSettings()
   * @returns {!ExecutionServerSettings} ExecutionServerSettings value
   */
  get (fbeValue = new ExecutionServerSettings()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelExecutionServerSettings}
   * @param {!ExecutionServerSettings} fbeValue ExecutionServerSettings value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.ExecutionPort.fbeSize) <= fbeStructSize) {
      fbeValue.ExecutionPort = this.ExecutionPort.get(6150)
    } else {
      fbeValue.ExecutionPort = 6150
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ExecutionPort.fbeSize

    if ((fbeCurrentSize + this.UseSsl.fbeSize) <= fbeStructSize) {
      fbeValue.UseSsl = this.UseSsl.get(false)
    } else {
      fbeValue.UseSsl = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UseSsl.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelExecutionServerSettings}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelExecutionServerSettings}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelExecutionServerSettings}
   * @param {!ExecutionServerSettings} fbeValue ExecutionServerSettings value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelExecutionServerSettings}
   * @param {ExecutionServerSettings} fbeValue ExecutionServerSettings value
   */
  setFields (fbeValue) {
    this.ExecutionPort.set(fbeValue.ExecutionPort)
    this.UseSsl.set(fbeValue.UseSsl)
  }
}

export { FieldModelExecutionServerSettings };

/**
 * Fast Binary Encoding ExecutionServerSettings model
 */
class ExecutionServerSettingsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelExecutionServerSettings(this.buffer, 4)
  }

  /**
   * Get the ExecutionServerSettings model
   * @this {!ExecutionServerSettingsModel}
   * @returns {!FieldModelExecutionServerSettings} model ExecutionServerSettings model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ExecutionServerSettingsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ExecutionServerSettingsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ExecutionServerSettingsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ExecutionServerSettingsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelExecutionServerSettings.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ExecutionServerSettingsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ExecutionServerSettingsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ExecutionServerSettingsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ExecutionServerSettingsModel}
   * @param {!ExecutionServerSettings} value ExecutionServerSettings value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ExecutionServerSettingsModel}
   * @param {!ExecutionServerSettings} value ExecutionServerSettings value, defaults is new ExecutionServerSettings()
   * @return {!object} Deserialized ExecutionServerSettings value and its size
   */
  deserialize (value = new ExecutionServerSettings()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ExecutionServerSettings(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ExecutionServerSettings(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ExecutionServerSettingsModel}
   * @param {!number} prev Previous ExecutionServerSettings model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ExecutionServerSettingsModel };
